import {
  Typography,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useAppTheme } from "core/contexts/theme/theme";
import { CustomTooltip, formatDate } from "../../home";
import {
  ConversationType,
  MessageType,
} from "modules/chat/components/chat.type";
import { useEffect, useState } from "react";
import {
  getConversationBrackets,
  getConversationDailyStats,
  getConversations,
} from "modules/agentMySecurity/services/embeddingsService";
import { CardHeader } from "../dashboard.styled";

// Conversation Analytics Tab
export const ConversationAnalyticsTab = () => {
  const { apptheme } = useAppTheme();
  const [loadingConversations, setAreConvosLoading] = useState(true);
  const [loadingMessages, setAreMessagesLoading] = useState(true);
  const [loadingMsgsPerUser, setAreloadingMsgsPerUser] = useState(true);
  const [loadingDailyStats, setIsloadingDailyStats] = useState(true);
  const [messagesPerUserData, setMessagesPerUserData] = useState([]);
  const [conversationDailyStats, setConversationDailyStats] = useState([]);

  async function fetchMessagesPerUserData() {
    try {
      // Fetch conversations from the API
      let brackets = await getConversationBrackets();
      setMessagesPerUserData(generateMessagesPerUserData(brackets));

      // Reset the loading
      setAreloadingMsgsPerUser(false);
    } catch (error) {
      // Reset the loading
      setAreloadingMsgsPerUser(true);
    }
  }

  async function fetchConversationDailyStats() {
    try {
      // Fetch conversations from the API
      const list = await getConversationDailyStats();
      setConversationDailyStats(list);

      // Reset the loading
      setIsloadingDailyStats(false);
    } catch (error) {
      // Reset the loading
      setIsloadingDailyStats(true);
    }
  }

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the list of conversations
        fetchMessagesPerUserData();

        // Get user brackets for conversations and average msgs per day
        fetchConversationDailyStats();
      } catch (error) {
        // Reset the loading
        setAreConvosLoading(false);
        setAreMessagesLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Grid container spacing={3}>
      {/* Conversation Trend */}
      <Grid item xs={12} md={8}>
        <Paper className="p-2">
          <CardHeader>Conversation Volume Trend</CardHeader>
          {loadingDailyStats ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={conversationDailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  label={{
                    angle: 270,
                    position: "left",
                    fill: apptheme.colors.textColor,
                  }}
                  stroke="white"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="totalConversations"
                  stroke="#8884d8"
                  name="Daily Conversations"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="avgMessagesPerConversation"
                  stroke="#82ca9d"
                  name="Avg Messages per Conversation"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* Messages Per User */}
      <Grid item xs={12} md={4}>
        <Paper className="p-2">
          <CardHeader>Messages/User</CardHeader>
          {loadingMsgsPerUser ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={messagesPerUserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis label={{ fill: apptheme.colors.textColor }} />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend />
                <Bar
                  dataKey="users"
                  name="Number of Users"
                  fill={apptheme.colors.barChartBarBg}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const generateMessagesPerUserData = (list) => {
  list = list.map((item, index) => {
    let lastItem;
    if (index > 0) {
      lastItem = list[index - 1];

      return {
        range: `${lastItem._id + 1}-${item._id}`,
        users: item.userCount,
      };
    } else {
      return {
        range: `1-10`,
        users: item.userCount,
      };
    }
  });
  return list;
};
